import { Injectable } from "@angular/core";
import { collection, doc, docSnapshots, DocumentData, Firestore, getDoc, getDocs, QuerySnapshot } from "@angular/fire/firestore";
import { Data, NavigationEnd, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { CatchErrorService } from "@core/catch-error.service";
import { isMobile } from "@core/utility.service";
import { getFunctions, httpsCallable } from "firebase/functions";
import { BehaviorSubject, combineLatest, defer, firstValueFrom, from, Observable, of, throwError } from "rxjs";
import { catchError, debounceTime, delay, distinctUntilChanged, filter, map, retryWhen, scan, shareReplay, startWith, switchMap, take } from "rxjs/operators";
import { CommonVariable } from "src/app/app.constants";
import { CommonService } from "src/app/services/common/common.service";
import { environment } from 'src/environments/environment';
import { UpdatePWAService } from "../../services/update-pwa.service";

@Injectable({
  providedIn: "root",
})
export class TabRepositoryService {
  tabSelected$ = new BehaviorSubject("home");
  redDotUnread$ = new BehaviorSubject(false);
  ringBell$ = new BehaviorSubject(false);
  private isNavigationInProgress = false;
  whatsNewVersion: any;
  appVersion: any;
  public unsplashCoverImages$: Observable<any> = this.catchService.isOnline$.pipe(
    switchMap((isOnline) => {
      if (!isOnline) {
        return of(null); // Return null if offline
      }

      const docRef = doc(this.firestore, "Global_lookup/unsplash_cover"); // Firestore document reference

      // Use Firestore's getDoc() to fetch the document
      return from(getDoc(docRef)).pipe(
        this.catchService.retryWhen,
        this.catchService.catchOfflineError
      );
    }),
    this.catchService.retryWhen,
    this.catchService.catchOfflineError,
    shareReplay(1) // Cache the observable for multiple subscribers
  );
  appCurrentVersion$ = this.catchService.isOnline$.pipe(
    switchMap((isOnline) => {
      if (!isOnline) return of(null); // Return null if offline

      const docRef = doc(this.firestore, environment.pwaVersionCheckPath); // Firestore document reference

      // Use Firestore's docSnapshots() for real-time updates
      return docSnapshots(docRef).pipe(
        this.catchService.retryWhen,
        this.catchService.catchOfflineError
      );
    }),
    this.catchService.retryWhen,
    this.catchService.catchOfflineError,
    shareReplay(1) // Cache the observable for multiple subscribers
  ).pipe(
    map((res: Data) => res?.data()) // Extract document data
  );

  defaultCoverImages$: Observable<any> = this.catchService.isOnline$.pipe(
    switchMap((isOnline) => {
      if (!isOnline) {
        return of({ list: [] }); // Return an initial empty list if offline
      }

      const docRef = doc(this.firestore, "/Global_lookup/cover_image"); // Firestore document reference

      // Use Firestore's docSnapshots() for real-time updates
      return docSnapshots(docRef).pipe(
        this.catchService.retryWhen,
        this.catchService.catchOfflineError,
        map((res: Data) => res?.data()) // Extract document data from snapshot (online path only)
      );
    }),
    this.catchService.retryWhen,
    this.catchService.catchOfflineError,
    shareReplay(1) // Cache the observable for multiple subscribers
  );
  tabsPages = ["social", "home", "syndicate", "game", "store", "home-feed"];
  public translationLoaded = new BehaviorSubject(false);
  constructor(
    private firestore: Firestore,
    private UpdatePWAService: UpdatePWAService,
    private router: Router,
    private catchService: CatchErrorService,
    private commonService: CommonService
  ) {
    this.router.events.pipe(
      startWith(this.router),
      debounceTime(50) // Add small debounce time to prevent rapid updates
    )?.subscribe((event: Data) => {
      if (event instanceof NavigationEnd && !this.isNavigationInProgress) {
        const matchingTab = this.tabsPages.find((tab) => this.router.url.includes("/mainTabs/tabs/" + tab));
        if (matchingTab) {
          // Only update if different to prevent circular updates
          if (this.tabSelected$.getValue() !== matchingTab) {
            this.tabSelected$.next(matchingTab);
          }
        }
      }
    });
  }


  // Note: Navigation is now handled by Ionic's declarative tab navigation (tab="..." attribute)
  // These methods only update the tab state for tracking purposes
  
  selectHomeTab() {
    this.tabSelected$.next("home");
  }

  selectSyndicateTab() {
    this.tabSelected$.next("syndicate");
  }

  selectGameTab() {
    this.tabSelected$.next("game");
  }

  selectStoreTab() {
    this.tabSelected$.next("store");
  }

  selectMenuTab() {
    this.tabSelected$.next("menu");
  }

  selectSocialTab() {
    this.tabSelected$.next("social");
  }

  getTabSelected() {
    return this.tabSelected$;
  }

  setRedDotUnread(value) {
    this.redDotUnread$.next(value);
  }

  setRingBell(value) {
    if (value == true && isMobile.Android()) {
      try {
        window.navigator.vibrate([800, 200, 800, 200, 800]);
      } catch (Error) {
        return Error;
      }
    }
    this.ringBell$.next(value);
  }
  checkWhatsNewVersion() {
    this.catchService.isOnline$
      .pipe(
        switchMap((isOnline) => {
          if (!isOnline) return of({ general: null, user: null });

          // Fetch general "What's New" version
          const generalDocRef = doc(this.firestore, `/general_modules/whats_new`);
          const general$ = docSnapshots(generalDocRef).pipe(
            this.catchService.retryWhen,
            this.catchService.catchOfflineError,
            map((snap: any) => snap?.data() || null)
          );

          // Directly combine with commonService.getUid without storing in a variable
          return combineLatest([
            general$,
            this.commonService.getUid("checkWhatsNewVersion").pipe(
              switchMap((userId) => {
                if (!userId) return of(null);

                const userDocRef = doc(this.firestore, `/users/${userId}/in_app_notification/status`);
                return docSnapshots(userDocRef).pipe(
                  this.catchService.retryWhen,
                  this.catchService.catchOfflineError,
                  map((snap: any) => ({
                    ...snap?.data(),
                    userId // ✅ attach userId here
                  }))
                );
              })
            )
          ]).pipe(
            map(([generalData, userData]) => ({ general: generalData, user: userData }))
          );
        }),
        shareReplay(1)
      )
      .subscribe({
        next: ({ general, user }) => {
          if (general?.version) {
            this.whatsNewVersion = general.version;
          }

          if (!user?.whats_new?.version || user.whats_new.version !== this.whatsNewVersion) {
            this.updateWhatsNewVersion();
          }
        },
        error: (err) => {
          console.error("Error checking What's New version:", err);
        }
      });
  }

  // Update the user's "What's New" version
  async updateWhatsNewVersion(): Promise<void> {
    try {
      if (!this.whatsNewVersion) return;

      const userId = await firstValueFrom(this.commonService.getUid("updateWhatsNewVersion"));
      if (!userId) {
        console.warn("User ID not found, skipping update of What's New version.");
        return;
      }
      const path_id = "KdpTQsnyjKvgqvjItVvT";
      const doc_data = { whats_new: { version: this.whatsNewVersion, show_notice: true } };
      const method = "update";

      await this.commonService.getV2CloudFunction("updatefirestoredoc", {
        path_id,
        doc_data,
        method,
      }).subscribe({
        error: (err) => {
          console.error('Error in updatefirestoredoc', err);
          throw err;
        }
      });
    } catch (err) {
      console.error("Error updating Whats New version:", err);
    }
  }


  // Update the user's "What's New" notice to hide it
  async updateWhatsNewNotice(): Promise<void> {
    try {
      if (!this.whatsNewVersion) return;

      // Get userId from commonService
      const userId = await firstValueFrom(this.commonService.getUid("updateWhatsNewNotice"));
      if (!userId) {
        console.warn("User ID not found, skipping update of Whats New notice.");
        return;
      }
      const path_id = "KdpTQsnyjKvgqvjItVvT";
      const doc_data = { whats_new: { version: this.whatsNewVersion, show_notice: false } };
      const method = "update";

      await this.commonService.getV2CloudFunction("updatefirestoredoc", {
        path_id,
        doc_data,
        method,
      }).subscribe({
        error: (err) => {
          console.error('Error in updatefirestoredoc', err);
          throw err;
        }
      });
    } catch (err) {
      console.error("Error updating Whats New notice:", err);
    }
  }

  checkUserVersion() {
    this.catchService.isOnline$
      .pipe(
        switchMap((isOnline) => {
          if (!isOnline) return of(null); // Handle offline scenario

          const docRef = doc(this.firestore, environment.pwaVersionCheckPath); // Firestore document reference
          return docSnapshots(docRef).pipe(
            this.catchService.retryWhen,
            this.catchService.catchOfflineError
          );
        }),
        this.catchService.retryWhen,
        this.catchService.catchOfflineError,
        shareReplay(1)
      )
      .pipe(
        map((res: any) => res?.data()) // Extract the document data
      )
      .subscribe(
        async (res: any) => {
          CommonVariable.appVersion = res && res?.version ? res.version : ""; // Assign PWA version

          const userId = await firstValueFrom(this.commonService.getUid("checkUserVersion"));
          if (!userId) return; // Handle missing userId

          this.catchService.isOnline$
            .pipe(
              switchMap((isOnline) => {
                if (!isOnline) return of(null);

                const userVersionRef = doc(
                  this.firestore,
                  `/users/${userId}/pwa_version/user_version` // Firestore reference for user version
                );

                // Convert the Promise (getDoc()) into Observable
                return from(getDoc(userVersionRef)).pipe(
                  this.catchService.retryWhen,
                  this.catchService.catchOfflineError,
                  // Handle document not found or permission denied gracefully
                  catchError((error) => {
                    console.warn('[TabRepository] Error fetching user version (expected for new users):', error?.message || error);
                    return of(null);
                  })
                );
              }),
              this.catchService.retryWhen,
              this.catchService.catchOfflineError,
              shareReplay(1)
            )
            .pipe(take(1)) // Take only the first emission
            .subscribe(
              (res: any) => {
                // Handle case where document doesn't exist or is null
                const data = res?.data();
                if (!data) {
                  console.log('[TabRepository] User version document not found or empty - using default');
                  return;
                }
                
                if (data?.version !== CommonVariable.appVersion) {
                  this.updatePwaVersion(CommonVariable.appVersion).subscribe(); // Update version if mismatched
                  this.UpdatePWAService.promptUser(); // Prompt the user to update
                }
              },
              (err) => {
                console.error("[TabRepository] Error fetching user version:", err); // Log errors but don't throw
                return err; // Return the error for handling
              }
            );
        },
        (err) => {
          console.error("Error fetching PWA version:", err); // Log errors
          return err; // Handle errors gracefully
        }
      );
  }

  public getV2CloudFunction(CloudFunctionName: string, payload: Data | string, options: Data = {}) {
    try {
      const functions = getFunctions();
      functions.region = AuthService.FIREBASE_REGION;
      return defer(() => {
        return from(httpsCallable(functions, CloudFunctionName, options)(payload));
      }).pipe(
        map((res: Data) => {
          return res?.data;
        }),
        retryWhen((errors) =>
          errors.pipe(
            scan((retryCount, error) => {
              if (retryCount >= 3) {
                throw error;
              }
              return retryCount + 1;
            }, 0),
            delay(1000) // Delay for retry in milliseconds
          )
        ),
        catchError((error) => {
          return throwError(new Error(`Error in ${CloudFunctionName}, Reason: ${error}`));
        })
      );
    } catch (error) {
      return throwError(error);
    }
  }

  // Update the PWA version in Firestore
  updatePwaVersion(appVersion: string) {
    return this.commonService.getUid("AccountRepositoryService").pipe(
      switchMap((userId) => {
        if (!appVersion || !userId) {
          console.error("App version or User ID is missing.");
          return of(null);
        }

        const path_id = "p7tvyag5rpi6mgeJ7oUu";
        const doc_data = { version: appVersion };
        const method = "update";

        return this.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          method,
        }).pipe(
          catchError((err) => {
            console.error("Error updating PWA version:", err);
            this.catchService.handleFirestoreError(err);
            return of(null);
          })
        );
      })
    );
  }

  // Tracked feature names from the global feature_review collection
  trackedFeatureNames$: Observable<string[]> = this.catchService.isOnline$.pipe(
    switchMap((isOnline): Observable<string[]> => {
      if (!isOnline) {
        return of([]);
      }
      const collectionRef = collection(this.firestore, "feature_review");
      return from(getDocs(collectionRef)).pipe(
        map((snapshot: QuerySnapshot<DocumentData>) =>
          snapshot.docs
            .map((docSnap) => {
              const featureName = docSnap.data()?.["feature_name"];
              return typeof featureName === "string" && featureName ? featureName : docSnap.id;
            })
            .filter((name) => !!name)
        ),
        catchError(() => of([]))
      );
    }),
    catchError(() => of([])),
    shareReplay(1)
  );

  // Get the list of restricted pages
  isRestrictedPageList$: Observable<any> = this.catchService.isOnline$.pipe(
    switchMap((isOnline) => {
      if (!isOnline) {
        return of({ list: [] }); // Return an initial empty list if offline
      }
      const docRef = doc(this.firestore, "/Global_lookup/restricted_page"); // Firestore document reference
      // Use Firestore's docSnapshots() for real-time updates
      return docSnapshots(docRef).pipe(
        this.catchService.retryWhen,
        this.catchService.catchOfflineError
      );
    }),
    this.catchService.retryWhen,
    this.catchService.catchOfflineError,
    shareReplay(1) // Cache the observable for multiple subscribers
  ).pipe(
    map((res: any) => res?.data() ?? { list: [] }), // Extract document data; missing doc is an empty list
  );

  // Check if the current route is restricted
  isRouteNotRestricted$: Observable<boolean> = combineLatest([
    this.isRestrictedPageList$,
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      startWith(this.router.url), // Emit initial URL
      map(() => this.router.url)
    )
  ]).pipe(
    map(([restrictedData, currentUrl]) => {
      const restrictedList: string[] = Array.isArray(restrictedData?.list) ? restrictedData.list : [];
      if (restrictedList.length === 0) {
        return false;
      }
      return restrictedList.some(path => currentUrl.includes(path));
    }),
    distinctUntilChanged()
  );

  userPwaVersion$ = this.catchService.isOnline$.pipe(
    switchMap((isOnline) => {
      if (!isOnline) return of(null);

      return from(this.commonService.getUid('checkUserVersion')).pipe(
        switchMap((userId) => {
          if (!userId) return of(null);

          const userVersionRef = doc(
            this.firestore,
            `/users/${userId}/pwa_version/user_version`
          );
          return docSnapshots(userVersionRef).pipe(
            this.catchService.retryWhen,
            this.catchService.catchOfflineError,
            // Handle document not found or permission denied gracefully
            catchError((error) => {
              console.warn('[TabRepository] userPwaVersion$ error (expected for new users):', error?.message || error);
              return of(null);
            })
          );
        })
      );
    }),
    this.catchService.retryWhen,
    this.catchService.catchOfflineError,
    map((res: Data) => res?.data()?.version || ''),
    startWith(''),
    shareReplay(1)
  );
}
